import { isSSR } from "../ssr";
import { digestSHA256 } from "../sha256";
import { WSPR } from "types/window";
import type { WebQuoteFormData } from "components/WebQuoteModal/WebQuoteForm/WebQuoteForm";
import type {
  FormTrackingEvent,
  HashedLeadFormDataPII
} from "@silversea-ssc/system-components-react/LeadFormBase/utils/tracking";
import * as AuthState from "../../features/auth/auth-state";
import { match, P } from "ts-pattern";
import { AvailableWebPrice } from "@silversea-ssc/dotcom-api-types/Prices";
import { Currency as CurrencyCode } from "src/utils/localization/currency";
import { CruisePriceAndOffersV2Response } from "@silversea-ssc/dotcom-api-types/CruisePriceAndOffersV2";
import { ActivePromoForCruise } from "../promos";

export async function trackWebQuoteFormSubmitted(
  event: FormTrackingEvent,
  data: WebQuoteFormData,
  wspr?: WSPR
): Promise<void> {
  const eventWithHashedPII = {
    ...event,
    ...(await webQuoteFormEventWithHashedPII(data))
  };

  (wspr || window.wspr)?.P("web_request_submitted_raw", eventWithHashedPII);
  // dispatchTrackingEvent("web-request-submitted", event);
}

const webQuoteFormEventWithHashedPII = async (
  data: WebQuoteFormData
): Promise<HashedLeadFormDataPII> => {
  return {
    NameFirst_hash: await digestSHA256(data.firstName),
    NameLast_hash: await digestSHA256(data.lastName),
    Email_hash: await digestSHA256(data.email),
    Phone_hash: await digestSHA256(data.phoneNumber)
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function pushToDataLayer(vals: any): void {
  if (!isSSR()) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(vals);
  }
}

export interface VoyageToDataLayerInterface {
  name?: string;
  code?: string;
  departure_date?: string;
  arrival_date?: string;
  days_duration?: number;
  ship_name?: string;
  price?: number;
  sale_state?: string;
  suite?: string;
  departure_port?: string;
  arrival_port?: string;
  destination_id?: number;
  destination_name?: string;
  cruise_type?: string;
}

type PromoRef = { code: string; title: string };

type DataLayerPrice = {
  type: AvailableWebPrice["type"];
  fare_code: AvailableWebPrice["webFareCode"];
  currency: CurrencyCode;
  amount: number | null;
  original_amount: number | null;
  promos: PromoRef[];
};

type VoyagePriceDataLayerValues = {
  price: DataLayerPrice;
  active_promos: PromoRef[];
};

type VoyagePriceDataLayer = {
  voyage_price: VoyagePriceDataLayerValues;
};

export function voyagePriceToDataLayer(
  price: CruisePriceAndOffersV2Response,
  activePromos: ActivePromoForCruise[],
  currencyCode: CurrencyCode
): void {
  if (!price.priceFrom) return;
  pushToDataLayer(voyagePrice(price.priceFrom, activePromos, currencyCode));
}

function voyagePrice(
  priceFrom: AvailableWebPrice,
  activePromos: ActivePromoForCruise[],
  currencyCode: CurrencyCode
): VoyagePriceDataLayer {
  return {
    voyage_price: {
      price: toDataLayerPrice(priceFrom, activePromos, currencyCode),
      active_promos: activePromos.map((x) => promoRef(x.code, activePromos))
    }
  };
}

const promoRef = (
  code: string,
  activePromos: ActivePromoForCruise[]
): PromoRef => {
  const promo = activePromos.find((p) => p.code === code);
  return { code, title: promo?.content.title?.localized || code };
};

const toDataLayerPrice = (
  price: AvailableWebPrice,
  activePromos: ActivePromoForCruise[],
  currency: CurrencyCode
): DataLayerPrice => {
  const base = {
    type: price.type,
    fare_code: price.webFareCode,
    currency,
    amount: null,
    original_amount: null,
    promos: []
  };

  return match(price)
    .with({ type: "price" }, ({ amount }) => ({
      ...base,
      amount,
      original_amount: amount
    }))
    .with({ type: "discounted" }, (p) => ({
      ...base,
      amount: p.amount,
      original_amount: p.originalAmount,
      promos: p.promoConfigurationCodes.map((code) =>
        promoRef(code, activePromos)
      )
    }))
    .with({ type: "waitlist" }, () => base)
    .exhaustive();
};

export function voyageToDataLayer(args: VoyageToDataLayerInterface): void {
  pushToDataLayer(voyageToTrackingEvent(args));
}

export const voyageToTrackingEvent = (args: VoyageToDataLayerInterface) => {
  const { sale_state, ...commonArgs } = args;
  return {
    voyage_details: {
      sale_state: sale_state == "price" ? "active" : sale_state,
      ...commonArgs
    }
  };
};

interface DestinationToDataLayerInterface {
  name?: string;
  destination_id?: number;
  destination_type: "Classic" | "Expedition";
}
export function destinationToDataLayer(
  args: DestinationToDataLayerInterface
): void {
  const { name, destination_id, destination_type } = args;
  pushToDataLayer({
    destination_details: {
      name,
      destination_id,
      destination_type
    }
  });
}

interface UserLoggedInDataLayer {
  user_details: {
    is_logged_in: true;
    vs_loyalty_id?: string;
    email?: string;
    consumer_id?: string;
    individual_id?: string;
    vdsid?: string;
  };
}

interface AnonymousUserDataLayer {
  user_details: {
    is_logged_in: false;
  };
}

export function userDetailsToDataLayer(state: AuthState.AuthState): void {
  const dataLayer = match(state)
    .with(
      { status: "authenticated" },
      ({ user }): UserLoggedInDataLayer => ({
        user_details: {
          is_logged_in: true,
          vs_loyalty_id: user.vsLoyaltyId ?? undefined,
          email: user.email,
          consumer_id: user.id,
          individual_id: user.individualId ?? undefined,
          vdsid: user.vdsid ?? undefined
        }
      })
    )
    .with(
      { status: "anonymous" },
      (): AnonymousUserDataLayer => ({ user_details: { is_logged_in: false } })
    )
    .with({ status: P.union("loading", "error") }, () => null)
    .exhaustive();

  if (!dataLayer) return;
  pushToDataLayer(dataLayer);
}
